import { CatalystInsightCategory, CatalystSubcategories } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { useMarkEventRead } from '~/react-query-hooks/events/use-event-feed/useEventFeed';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { useMarketCatalystDrawer } from '~/widgets/market-catalyst-drawer/useMarketCatalystDrawer';

import { CatalystInsightIcon } from '../catalyst-insight-icon/CatalystInsightIcon';
import { getCatalystInsightIconProps } from '../catalyst-insight-icon/utils';
import { CatalystInsightTooltip } from '../catalyst-insight-tooltip/CatalystInsightTooltip';
import { useCatalystDrawer } from '../useCatalystDrawer';
import * as S from './CatalystInsightPill.styles';

const COMPANY_CATALYSTS = 'company';

export interface CatalystInsightPillProps {
  insightId: string;
  entityTag?: string;
  themeId?: string;
  isClickable?: boolean;
  source: PosthogTrackingSource;
  category: CatalystInsightCategory;
  subCategory?: CatalystSubcategories;
  eventId?: string;
  read?: boolean;
}

export const CatalystInsightPill = ({
  insightId,
  entityTag,
  themeId,
  subCategory,
  category,
  isClickable = false,
  source,
  eventId = '',
  read = false,
}: CatalystInsightPillProps) => {
  const { t } = useTranslation(['insights']);
  const { openDrawer: openCompanyCatalystDrawer } = useCatalystDrawer();
  const { openDrawer: openMarketCatalystDrawer } = useMarketCatalystDrawer();
  const { mutate: markEventRead } = useMarkEventRead();

  const isCompanyCatalyst = category === COMPANY_CATALYSTS;

  const handleClick = () => {
    if (isClickable) {
      if (eventId) {
        markEventRead(eventId);
      }

      if (isCompanyCatalyst && entityTag) {
        openCompanyCatalystDrawer({
          type: category,
          entityTag,
          insightId,
          source,
        });
      } else if (subCategory) {
        openMarketCatalystDrawer({
          type: subCategory,
          themeId,
          insightId,
          source,
        });
      }
    }
  };

  return (
    <TooltipContainer
      label={
        <CatalystInsightTooltip
          subCategory={subCategory}
          isRead={read}
          iconProps={getCatalystInsightIconProps({
            isBordered: false,
            isCompanyCatalyst,
            isRead: read,
          })}
          category={category}
        />
      }
      placement="top-start"
      offsetY={-5}
      offsetX={8}
      hasMaxWidth={false}
      disabled={!isClickable}
      inPortal
      withSafePolygon
    >
      <S.CatalystInsightWrapper
        data-testid="catalyst-insight-pill"
        $isClickable={isClickable}
        $isCompanyCatalyst={isCompanyCatalyst}
        $isRead={read}
        onClick={handleClick}
      >
        <CatalystInsightIcon
          size={12}
          enableHover={false}
          isBordered={false}
          catalystInsightId={insightId}
          category={category}
          subCategory={subCategory}
          read={read}
          source={source}
        />
        <S.CatalystInsightLabel
          $isCompanyCatalyst={isCompanyCatalyst}
          $isRead={read}
        >
          {isCompanyCatalyst
            ? t('insights:catalyst.titleCompanyCatalyst')
            : t('insights:catalyst.titleMarketCatalyst')}
        </S.CatalystInsightLabel>
      </S.CatalystInsightWrapper>
    </TooltipContainer>
  );
};
